import type {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ISetFilterParams,
  ValueGetterParams,
} from 'ag-grid-community'

import type { Signal, SignalType } from '@/gen/inventory/v1/signal_pb'

import { AlertNameCell } from './alert-name-cell'
import {
  aiGovernanceTypeTitleIcon,
  alertNameParts,
  distinctSignalTypeTitles,
  signalTypeTitleOf,
} from './column'

type AlertNameColumnOptions<TRow> = {
  colId: string
  headerName: string
  // The filter bar names the column after what it filters on.
  filterName: string
  rows: readonly TRow[]
  titleByType: ReadonlyMap<SignalType, string>
  getSignal: (row: TRow | undefined) => Signal | undefined
  flex?: number
  minWidth?: number
  width?: number
  subtitleClassName?: string
}

export const alertNameColumnDef = <TRow,>({
  colId,
  headerName,
  filterName,
  rows,
  titleByType,
  getSignal,
  flex,
  minWidth,
  width,
  subtitleClassName,
}: AlertNameColumnOptions<TRow>): ColDef<TRow> => ({
  colId,
  headerName: filterName,
  headerComponentParams: { innerHeaderComponent: () => <>{headerName}</> },
  flex,
  minWidth,
  width,
  cellRenderer: (params: ICellRendererParams<TRow>) => (
    <AlertNameCell
      signal={getSignal(params.data)}
      titleByType={titleByType}
      subtitleClassName={subtitleClassName}
    />
  ),
  sortable: true,
  filter: 'agSetColumnFilter',
  // Filters by type only; an alert's own name is found through the search bar.
  filterValueGetter: (params: ValueGetterParams<TRow>) =>
    signalTypeTitleOf(getSignal(params.data ?? undefined), titleByType),
  filterParams: {
    values: distinctSignalTypeTitles(rows.map(getSignal), titleByType),
    getIcon: aiGovernanceTypeTitleIcon(rows.map(getSignal), titleByType),
    suppressMiniFilter: true,
    selectAllOnMiniFilter: true,
    suppressSyncValuesAfterDataChange: true,
  } as ISetFilterParams,
  getQuickFilterText: (params: GetQuickFilterTextParams<TRow>) => {
    const signal = getSignal(params.data)
    const { title, description } = alertNameParts(signal, titleByType)
    return `${title} ${description} ${signal?.title ?? ''}`
  },
  valueGetter: (params: ValueGetterParams<TRow>) =>
    alertNameParts(getSignal(params.data ?? undefined), titleByType).title,
})
